/**
 * `useHashedSessionKey` — React hook that returns a stable, tenant-safe
 * hashed identifier for the current Clerk session, or `undefined` when the
 * user is not authenticated OR while the hash is still being computed.
 *
 * @remarks
 * The docs slider and chatbot dock never see the raw `ownerId`; instead they
 * receive `sessionId = hashSessionKey(user.id)` (a 16-char lowercase hex
 * string). This hook centralises that derivation so every consumer inside
 * the authenticated shell reads the same value, in line with Constitution
 * Rule II (multi-tenant isolation by `ownerId`).
 *
 * The hook is a thin wrapper around {@link hashSessionKey} that:
 *
 * - Reads the Clerk session via `useUser`.
 * - Runs the async hash in `useEffect` when `user.id` changes and stores
 *   the result in state, so re-renders return a stable string reference.
 * - Returns `undefined` while the hash is pending, and also when the user
 *   is signed-out, `user === null`, or has no `id`.
 *
 * @returns A memoised 16-char lowercase hex string once the hash resolves,
 *   or `undefined` when signed out / while pending.
 */

import { useEffect, useState } from 'react';
import { useUser } from '@clerk/clerk-react';

import { hashSessionKey } from '../help-docs-panel/hashSessionKey';

export function useHashedSessionKey(): string | undefined {
  const { user } = useUser();
  const userId: string | undefined = user?.id;

  const [key, setKey] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (userId === undefined) {
      // Clear the previous key if the user signs out or loses their id
      // between renders. `setKey(undefined)` when state is already
      // `undefined` is a no-op thanks to React's Object.is bail-out.
      setKey(undefined);
      return undefined;
    }

    let cancelled = false;
    hashSessionKey(userId)
      .then((hashed) => {
        if (!cancelled) {
          setKey(hashed);
        }
      })
      .catch(() => {
        // `hashSessionKey` only rejects on the empty-string edge case,
        // which the `userId === undefined` guard already excludes. A
        // catch is kept so any future runtime failure (e.g. a browser
        // without Web Crypto) degrades to `undefined` rather than an
        // unhandled promise rejection.
        if (!cancelled) {
          setKey(undefined);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  return key;
}
